import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'

const ChangePasswordPage = () => {
  const { t } = useTranslation('auth')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [validationErrors, setValidationErrors] = useState({})

  const { changePassword, user, loading: authLoading } = useAuth()
  const navigate = useNavigate()

  // Redirect to dashboard once user is authenticated
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard', { replace: true })
    }
  }, [user, authLoading, navigate])

  const validatePassword = (password) => {
    const errors = {}

    if (password.length < 8) {
      errors.length = t('changePassword.requirementLength')
    }
    if (!/[A-Z]/.test(password)) {
      errors.uppercase = t('changePassword.requirementUppercase')
    }
    if (!/[a-z]/.test(password)) {
      errors.lowercase = t('changePassword.requirementLowercase')
    }
    if (!/[0-9]/.test(password)) {
      errors.number = t('changePassword.requirementNumber')
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      errors.special = t('changePassword.requirementSpecial')
    }

    return errors
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setValidationErrors({})

    // Validate password requirements
    const errors = validatePassword(newPassword)
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      return
    }

    // Check if passwords match
    if (newPassword !== confirmPassword) {
      setError(t('errors.passwordMismatch'))
      return
    }

    setLoading(true)

    try {
      // The NEW_PASSWORD_REQUIRED challenge session is held in AuthContext
      // (currentChallengeSession) by the custom-auth flow; the legacy Amplify
      // cognitoUser is no longer used. If there's no active challenge,
      // changePassword surfaces that itself.
      const result = await changePassword(null, newPassword)

      if (result.success) {
        // Don't navigate here - let the useEffect handle it after user state is set
        // This prevents race condition between state update and navigation
        return
      } else {
        setError(result.error || t('errors.failedToChangePassword'))
      }
    } catch (err) {
      setError(err.message || t('errors.failedToChangePassword'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      padding: '1rem',
      backgroundColor: '#f5f5f5'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '500px',
        padding: '2rem',
        backgroundColor: 'white',
        border: '1px solid var(--color-border)',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h1 style={{ marginBottom: '1rem', textAlign: 'center' }}>{t('changePassword.title')}</h1>

        <p style={{
          marginBottom: '2rem',
          textAlign: 'center',
          color: '#666',
          fontSize: '0.9rem'
        }}>
          {t('changePassword.subtitle')}
        </p>

        {error && (
          <div style={{
            padding: '0.75rem',
            marginBottom: '1rem',
            backgroundColor: '#fee',
            color: '#c00',
            borderRadius: '4px',
            fontSize: '0.9rem'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              {t('changePassword.newPasswordLabel')}
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--color-border)',
                borderRadius: '4px'
              }}
              disabled={loading}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              {t('changePassword.confirmPasswordLabel')}
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--color-border)',
                borderRadius: '4px'
              }}
              disabled={loading}
            />
          </div>

          {Object.keys(validationErrors).length > 0 && (
            <div style={{
              padding: '1rem',
              marginBottom: '1rem',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              fontSize: '0.85rem'
            }}>
              <strong>{t('changePassword.requirementsTitle')}</strong>
              <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem' }}>
                {Object.values(validationErrors).map((error, idx) => (
                  <li key={idx} style={{ color: '#856404', marginTop: '0.25rem' }}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <div style={{
            padding: '1rem',
            marginBottom: '1.5rem',
            backgroundColor: '#e7f3ff',
            border: '1px solid #b3d9ff',
            borderRadius: '4px',
            fontSize: '0.85rem',
            color: '#004085'
          }}>
            <strong>{t('changePassword.mustContain')}</strong>
            <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem' }}>
              <li>{t('changePassword.requirementLength')}</li>
              <li>{t('changePassword.requirementUppercase')}</li>
              <li>{t('changePassword.requirementLowercase')}</li>
              <li>{t('changePassword.requirementNumber')}</li>
              <li>{t('changePassword.requirementSpecial')}</li>
            </ul>
          </div>

          <button
            type="submit"
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: '500'
            }}
            disabled={loading}
          >
            {loading ? t('changePassword.changingPassword') : t('changePassword.submitButton')}
          </button>
        </form>
      </div>
    </div>
  )
}

export default ChangePasswordPage
